import { AppThunk, RootState } from './../../rootReducer';
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
// import * as cookie from 'react-cookies';
import API, { ThenArg } from '../../services/API';
import {
  ErrorTypeAPI,
  startFetching,
  stopFetching,
} from '../../utilities/redux';
import { loadCartCookie, removeCartCookie, saveCartCookie } from './helpers';
import { langSelector } from '../App/selectors';
import { prop, arrayToObject, round } from '../../utilities';
import { createNewCart } from './utilities';

import { getProductDefaultInfo } from 'eshop-defaults/lib/utilities/selectors';
import {
  fbAddToCart,
  fbPurchase,
  ga4AddToCart,
  gaAddToCart,
  gaPurchase,
  // heurekaPurchase,
} from '@bart.sk-ecommerce/react-online-marketing';
import { getGoogleAdsId } from '../../configureTagManager';

import { heurekaPurchase } from '../../utilities/heureka-ads';

interface CartState {
  data: ThenArg<typeof API.getCart> | null;
  error: ErrorTypeAPI;
  isFetching: boolean;
  showProblems: boolean;
  deliveryPaymentInfo: {
    data: ThenArg<typeof API.getDeliveryPaymentInfo> | null;
    byId: Record<string, ThenArg<typeof API.getDeliveryPaymentInfo>> | null;
    ids: string[];
    isFetching: boolean;
    error: ErrorTypeAPI;
    allPostaPickupPoints: [] | null;
  };
  wasAddedToCartModal: boolean;
  wasAddedToCartModalProduct: any;
  wasAddedToCartModalCount: number;
  freeDelivery: ThenArg<typeof API.getFreeDeliveryInfo> | null;
}

const initialState: CartState = {
  data: null,
  isFetching: false,
  error: null,
  showProblems: false,
  deliveryPaymentInfo: {
    data: null,
    isFetching: false,
    error: null,
    byId: null,
    ids: [],
    allPostaPickupPoints: [],
  },
  wasAddedToCartModal: false,
  wasAddedToCartModalProduct: null,
  wasAddedToCartModalCount: 0,
  freeDelivery: null,
};

const cart = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setShowProblems(state, action: PayloadAction<{ show: boolean }>) {
      state.showProblems = action.payload.show;
    },

    setInitialCart(state) {
      state.data = null;
      state.error = null;
      stopFetching(state, '');
    },
    startFetchingCart(state) {
      startFetching(state, '');
    },
    fetchCartSuccess(
      state,
      action: PayloadAction<{
        cart: ThenArg<typeof API.getCart>;
      }>,
    ) {
      state.data = action.payload.cart;
      state.error = null;
      stopFetching(state, '');
    },
    fetchCartError(state, action: PayloadAction<ErrorTypeAPI>) {
      // check if it is not 404
      if (action.payload?.status !== 404) {
        state.error = action.payload;
      }
      stopFetching(state, '');
    },

    startFetchingDelPayInfo(state) {
      startFetching(state, 'deliveryPaymentInfo');
    },
    fetchDelPayInfoSuccess(
      state,
      action: PayloadAction<{
        ids: string[];
        deliveriesById: Record<
          string,
          ThenArg<typeof API.getDeliveryPaymentInfo>
        >;
        allPostaPickupPoints: [];
      }>,
    ) {
      state.deliveryPaymentInfo.ids = action.payload.ids;
      state.deliveryPaymentInfo.byId = action.payload.deliveriesById;
      state.deliveryPaymentInfo.allPostaPickupPoints =
        action.payload.allPostaPickupPoints;
      stopFetching(state, 'deliveryPaymentInfo');
    },
    fetchDelPayInfoError(state, action: PayloadAction<ErrorTypeAPI>) {
      // state.data = null;
      state.error = action.payload;
      stopFetching(state, 'deliveryPaymentInfo');
    },
    setWasAddedToCartModal(
      state,
      action: PayloadAction<{ product?: any; count?: number }>,
    ) {
      state.wasAddedToCartModal = action.payload.product
        ? !state.wasAddedToCartModal
        : false;

      state.wasAddedToCartModalProduct = action.payload.product || null;
      state.wasAddedToCartModalCount = action.payload.count || 0;
    },
    setFreeDelInfo(
      state,
      action: PayloadAction<{
        freeDelInfo: ThenArg<typeof API.getFreeDeliveryInfo>;
      }>,
    ) {
      state.freeDelivery = action.payload.freeDelInfo || null;
    },
  },
});

export const {
  startFetchingCart,
  fetchCartSuccess,
  fetchCartError,
  startFetchingDelPayInfo,
  fetchDelPayInfoSuccess,
  fetchDelPayInfoError,
  setWasAddedToCartModal,
  setInitialCart,
  setFreeDelInfo,
  setShowProblems,
} = cart.actions;
export default cart.reducer;

const cartDomainSelector = (state: RootState) => state.cart;

export const cartDataSelector = createSelector(cartDomainSelector, substate =>
  prop(substate, 'data', null),
);

export const cartIdSelector = createSelector(cartDomainSelector, substate =>
  prop(substate, 'data.id', null),
);

export const cartIsFetchingSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'isFetching', null),
);

export const cartErrorSelector = createSelector(cartDomainSelector, substate =>
  prop(substate, 'error', null),
);

export const cartDelPayInfoIdsSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'deliveryPaymentInfo.ids', null),
);

export const cartDelPayInfoByIdSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'deliveryPaymentInfo.byId', null),
);

export const cartPostaPickupPointsSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'deliveryPaymentInfo.allPostaPickupPoints', null),
);

export const cartDelPayInfoIsFetchingSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'deliveryPaymentInfo.isFetching', null),
);

export const cartShowProblemsSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'showProblems', false),
);

export const wasAddedToCartModalProductSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'wasAddedToCartModalProduct', null),
);

export const wasAddedToCartModalSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'wasAddedToCartModal', false),
);

export const wasAddedToCartModalCountSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'wasAddedToCartModalCount', 0),
);

export const wasAddedToCartModalIsFetchingSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'wasAddedToCartModalFetching', false),
);

export const wasAddedToCartModalBoughtTooSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'wasAddedToCartModalBoughtToo', null),
);

export const freeDelRemainingSelector = createSelector(
  cartDomainSelector,
  substate => prop(substate, 'freeDelivery.remaining_price', null),
);

export const addGiftCard = (cardNumber: string): AppThunk => async (
  dispatch,
  getState,
  API,
) => {
  const state = getState();
  const { id } = cartDataSelector(state);

  try {
    dispatch(startFetchingCart());

    let cart = await API.addCard(
      id,
      {},
      {
        card_type: 'GIFTCARD',
        card_number: cardNumber,
      },
    );
    cart = await API.getCart(id, { vatGroups: 1 });

    dispatch(fetchCartSuccess({ cart }));
  } catch (err) {
    dispatch(fetchCartError(err));
  }
};

export const removeGiftCard = (cardNumber: string): AppThunk => async (
  dispatch,
  getState,
  API,
) => {
  const state = getState();
  const { id } = cartDataSelector(state);

  try {
    dispatch(startFetchingCart());

    const cart = await API.removeCard(id, 'giftcard', cardNumber);

    dispatch(fetchCartSuccess({ cart }));
  } catch (err) {
    dispatch(fetchCartError(err));
  }
};

export const setWasAddedToCartModalVisibility = (
  product?: any,
  count?: number,
): AppThunk => dispatch => {
  dispatch(setWasAddedToCartModal({ product, count }));
};

export const fetchCart = (
  refresh: boolean = false,
  cartCookie?: string,
): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const userCartId = prop(state, 'auth.user.cartId');
  const cookieCartId = loadCartCookie(cartCookie);
  const freeDel = prop(state, 'cart.freeDelivery');

  if (
    refresh ||
    (userCartId && userCartId !== cookieCartId) ||
    !cartDataSelector(state)
  ) {
    let cartId: string | undefined = userCartId || cookieCartId;

    if (!cartId) {
      return;
    }

    if (cartId) {
      if (!freeDel) {
        const freeDelInfo = await API.getFreeDeliveryInfo(cartId, {});
        dispatch(setFreeDelInfo({ freeDelInfo }));
      }

      saveCartCookie(cartId);
      try {
        dispatch(startFetchingCart());
        const cart = await API.getCart(cartId, { vatGroups: 0 });
        dispatch(fetchCartSuccess({ cart }));
      } catch (err) {
        if (err.details.status === 404) {
          const id = await createNewCart(state, API);
          removeCartCookie();
          saveCartCookie(id);
          dispatch(fetchCart(refresh));
        } else {
          if (
            !prop(err, 'details.description').includes(
              'Nie je možné pridať takéto množstvo',
            )
          ) {
            dispatch(fetchCartError(err));
          }

          if (window) {
            window.alert(
              prop(
                err,
                'details.description',
                'Nastala chyba pri pridávaní do košíka',
              ),
            );
          }
        }
      }
    }
  }
};

export const addItemToCartFromModal = (count: number = 1): AppThunk => async (
  dispatch,
  getState,
) => {
  const state = getState();
  const product = wasAddedToCartModalProductSelector(state);
  const { productId, goodId } = getProductDefaultInfo(product);

  let cartIdCookie = loadCartCookie();
  let cartId = prop(cartDataSelector(state), 'id');

  if (cartId && !cartIdCookie) {
    saveCartCookie(cartId);
  }
  if (!cartId) {
    cartId = await createNewCart(state, API);
  }

  try {
    dispatch(startFetchingCart());
    const cart = await API.addCartItem(
      cartId,
      goodId,
      productId,
      {
        count,
        isDemand: 0,
        currencyId: 'EUR',
      },
      { xAcceptLanguage: langSelector(state) },
    );

    // if (window.gtag && gProduct && gProduct[0]) {
    if (window.gtag) {
      // ga4AddToCart(
      //   gProduct[0].good.product_id,
      //   gProduct[0].product.name,
      //   count,
      //   gProduct[0].good.final_price,
      //   getGoogleAdsId(),
      //   'EUR',
      // );
      gaAddToCart(product, getGoogleAdsId());
    }

    if (window.fbq) {
      fbAddToCart(product);
    }

    dispatch(fetchCartSuccess({ cart }));
    const freeDelInfo = await API.getFreeDeliveryInfo(cartId, {});
    dispatch(setFreeDelInfo({ freeDelInfo }));
  } catch (err) {
    if (err.details.status === 404) {
      const id = await createNewCart(state, API);
      removeCartCookie();
      saveCartCookie(id);
      dispatch(addItemToCartFromModal(count));
    } else {
      if (
        !prop(err, 'details.description').includes(
          'Nie je možné pridať takéto množstvo',
        )
      ) {
        dispatch(fetchCartError(err));
      }

      if (window) {
        window.alert(
          prop(
            err,
            'details.description',
            'Nastala chyba pri pridávaní do košíka',
          ),
        );
      }
    }
  }
};

export const addItemToCart = (
  product: any,
  goodId: number,
  productId: number,
  count: number = 1,
  isDemand: boolean = false,
  openModalAfter: boolean = true,
): AppThunk => async (dispatch, getState) => {
  const state = getState();

  let cartIdCookie = loadCartCookie();
  let cartId = prop(cartDataSelector(state), 'id');

  if (cartId && !cartIdCookie) {
    saveCartCookie(cartId);
  }
  if (!cartId) {
    cartId = await createNewCart(state, API);
  }

  try {
    dispatch(startFetchingCart());
    const cart = await API.addCartItem(
      cartId,
      goodId,
      productId,
      {
        count,
        isDemand: isDemand ? 1 : 0,
        currencyId: 'EUR',
        vatGroups: 0,
      },
      { xAcceptLanguage: langSelector(state) },
    );

    const gProduct = cart.items.filter(p => p.good.good_id === goodId);

    // if (window.gtag && gProduct && gProduct[0]) {
    if (window.gtag) {
      // ga4AddToCart(
      //   gProduct[0].good.product_id,
      //   gProduct[0].product.name,
      //   count,
      //   gProduct[0].good.final_price,
      //   getGoogleAdsId(),
      //   'EUR',
      // );
      gaAddToCart(product, getGoogleAdsId());
    }

    if (window.fbq) {
      fbAddToCart(product);
    }

    dispatch(fetchCartSuccess({ cart }));
    if (openModalAfter) {
      dispatch(setWasAddedToCartModalVisibility(product, count));
    }

    const freeDelInfo = await API.getFreeDeliveryInfo(cartId, {});
    dispatch(setFreeDelInfo({ freeDelInfo }));
  } catch (err) {
    if (err.details.status === 404) {
      const id = await createNewCart(state, API);
      removeCartCookie();
      saveCartCookie(id);
      dispatch(
        addItemToCart(
          product,
          goodId,
          productId,
          count,
          isDemand,
          openModalAfter,
        ),
      );
    } else {
      if (
        !prop(err, 'details.description').includes(
          'Nie je možné pridať takéto množstvo',
        )
      ) {
        dispatch(fetchCartError(err));
      }

      if (window) {
        window.alert(
          prop(
            err,
            'details.description',
            'Nastala chyba pri pridávaní do košíka',
          ),
        );
      }
    }
  }
};

export const removeItemFromCart = (
  goodId: number,
  productId: number,
  isRequest?: boolean,
): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const { id } = cartDataSelector(state);

  try {
    dispatch(startFetchingCart());
    const cart = await API.deleteCartItem(id, productId, goodId, {
      isDemand: isRequest ? 1 : 0,
      vatGroups: 0,
    });
    dispatch(fetchCartSuccess({ cart }));

    const freeDelInfo = await API.getFreeDeliveryInfo(id, {});
    dispatch(setFreeDelInfo({ freeDelInfo }));
  } catch (err) {
    dispatch(fetchCartError(err));
  }
};

export const changeItemNote = (
  goodId: number,
  note: string,
): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const { id } = cartDataSelector(state);

  try {
    dispatch(startFetchingCart());
    const cart = await API.addItemNote(
      id,
      goodId,
      { vatGroups: 0 },
      { note },
      { xAcceptLanguage: langSelector(state) },
    );
    dispatch(fetchCartSuccess({ cart }));
  } catch (err) {
    dispatch(fetchCartError(err));
  }
};

export const changeCartNote = (note: string): AppThunk => async (
  dispatch,
  getState,
) => {
  const state = getState();
  const { id } = cartDataSelector(state);

  try {
    dispatch(startFetchingCart());
    const cart = await API.addNote(
      id,
      { vatGroups: 0 },
      { note },
      { xAcceptLanguage: langSelector(state) },
    );
    dispatch(fetchCartSuccess({ cart }));
  } catch (err) {
    dispatch(fetchCartError(err));
  }
};

export const updateCart = (
  data: any,
  vatGroups: boolean = false,
  setAfterUpdate: boolean = true,
): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const cartData = cartDataSelector(state);

  try {
    if (setAfterUpdate) {
      dispatch(startFetchingCart());
    }
    const cart = await API.updateCart(
      cartData.id,
      { vatGroups: vatGroups ? 1 : 0 },
      data,
    );
    if (setAfterUpdate) {
      dispatch(fetchCartSuccess({ cart }));
    } else {
      dispatch(
        fetchCartSuccess({ cart: { ...cartData, problems: cart.problems } }),
      );
    }
  } catch (err) {
    if (setAfterUpdate) {
      dispatch(fetchCartError(err));
    }
  }
};

export const fetchDeliveryPaymentInfo = (): AppThunk => async (
  dispatch,
  getState,
) => {
  const state = getState();
  const { id } = cartDataSelector(state);

  try {
    dispatch(startFetchingDelPayInfo());
    const info = await API.getDeliveryPaymentInfo(
      id,
      {},
      { xAcceptLanguage: langSelector(state) },
    );

    const deliveriesById = arrayToObject(
      prop(info, 'delivery', []),
      'delivery_id',
    );
    const ids = prop(info, 'delivery', []).map(d => d.delivery_id);
    const allPostaPickupPoints = prop(info, 'allPostaPickupPoints', []);
    dispatch(
      fetchDelPayInfoSuccess({ ids, deliveriesById, allPostaPickupPoints }),
    );
  } catch (err) {
    dispatch(fetchDelPayInfoError(err));
  }
};

export const showProblemsInCart = (
  show: boolean,
): AppThunk => async dispatch => {
  dispatch(setShowProblems({ show }));
};

export const resetCart = (): AppThunk => async (dispatch, getState, API) => {
  const state = getState();

  dispatch(setInitialCart());
  await createNewCart(state, API);
};

export const handlePurchase = (order: any) => {
  return async (dispatch, getState, API) => {
    if (order && order.analytics_sended === 0) {
      const items: any[] = [];
      let sumOfItemsPriceWithoutVat = 0;
      let sumOfItemsPriceWithVat = 0;

      for (const item of order.items) {
        if (!item.product_id) {
        } else {
          sumOfItemsPriceWithoutVat += item.totalprice_without_vat;
          sumOfItemsPriceWithVat += item.totalprice;

          items.push({
            id: item.product_id,
            name: item.product_name,
            brand: '',
            category: null,
            quantity: parseInt(item.quantity, 10),
            price: round(item.totalprice / item.quantity, 2).toString(),
          });
        }
      }

      // const total = order.order_tprice;
      // const totalWithoutVat = order.order_tprice_without_vat;
      // const vatSum = total - totalWithoutVat;
      // const shipping = order.payment.delivery_price;
      // if (window && window.gtag) {
      /// @ts-ignore
      // let gData = order.items.filter(d => !d.isFromSale || d.product_id);
      // const tax = order.order_tprice - order.order_tprice_without_vat;
      // ga4Purchase(
      //   gData,
      //   order.order_nr,
      //   shipping,
      //   round(total, 2),
      //   round(vatSum, 2),
      //   getGoogleAdsId(),
      //   'EUR',
      // );
      // }

      gaPurchase(order, getGoogleAdsId());
      fbPurchase(order);
      heurekaPurchase(order);

      if (window && window.gtag) {
        window.gtag('event', 'purchase', {
          transaction_id: order.order_id,
          affiliation: 'Zlatapriadka.sk eshop',
          value: order.order_tprice.toFixed(2),
          currency: 'EUR',
          tax: (sumOfItemsPriceWithVat - sumOfItemsPriceWithoutVat).toFixed(2),
          shipping:
            +order.payment.delivery_price + +order.payment.payment_price,
          items,
        });
      }

      try {
        await API.updateOrderAnalytics(order.public_id);
      } catch (e) {
        console.error('Update order analytics failed!');
      }
    }
  };
};
